import { both, complement, isNil, isEmpty, includes, propOr, compose, __, equals, pick, pathOr } from 'ramda';
import memoize from 'memoizee';
import moment from 'moment';
import momentDurationFormatSetup from 'moment-duration-format';
import * as dateFns from 'date-fns';
import { zonedTimeToUtc, utcToZonedTime } from 'date-fns-tz';
import { DashboardViewConfig, WidgetDateFilter } from '../types/dashboard.types';
import { endOfMonth, endOfQuarter, startOfDay, startOfMonth, startOfQuarter, subMonths, subQuarters } from 'date-fns';
import { DateRange } from '../utils/util.type';
// @ts-ignore
momentDurationFormatSetup(moment);

const isValueExist = both(complement(isNil), complement(isEmpty));

const YEAR = 365;

const DATEFILTER_STATUS_VALUE_MAP = {
  YTD: 'year',
  MTD: 'month',
  WTD: 'isoWeek',
};

const VIEW_DEFAULT_DATE_FILTR = {
  type: 'relative',
  values: {
    relative: { unit: 'D', value: 30 },
  },
  todayExcluded: true,
};

const DEFAULT_TIME_ZONE = 'Asia/Seoul';

const commDateFormat = (() => {
  let lastLocale = '';

  return (date, format, locale) => {
    if (locale && typeof locale === 'string' && lastLocale !== locale) {
      lastLocale = locale;
      setMomentLocale(locale);
    }

    return format ? moment(date).format(format) : moment(date);
  };
})();

// const formatter: (any) => string = curryN(3, commDateFormat)(__, 'YYYY-MM-DD');
const formatter = date => commDateFormat(date, 'YYYY-MM-DD', undefined) as string;

function makeDurationFormat(duration, format) {
  return moment.duration(duration).format(format, { trim: false });
}

function makeDurationMilliseconds(duration) {
  return moment.duration(duration).asMilliseconds();
}

const pastNDays = (n = 0, date = undefined) => moment(date).subtract(n, 'days').toDate();

const setMomentLocale = locale => {
  moment.locale(locale);
};

const daysDiff = ({ from, to }) => moment(to).diff(moment(from), 'days');

const endOfDay = d => moment(d).endOf('day').toDate();

const isPast = d => moment(d).isBefore(new Date());

function getDefaultDate(): [string, string] {
  return [formatter(pastNDays(30)), formatter(new Date())];
}

const getStartOrEndDateOfDateUnit = ({ date, dateUnit, startDate = true }) => {
  if (dateUnit === 'week') {
    return startDate
      ? moment(date).startOf('isoWeek').format('YYYY-MM-DD')
      : moment(date).endOf('isoWeek').format('YYYY-MM-DD');
  } else if (dateUnit === 'month') {
    return startDate
      ? moment(date).startOf('month').format('YYYY-MM-DD')
      : moment(date).endOf('month').format('YYYY-MM-DD');
  } else {
    return date;
  }
};

const getDateGroupUnit = ({ from, to }) => {
  if (!from || !to) return 'day';

  let duration = moment(to).diff(from, 'days');

  let dateGroup;

  if (duration < 90) {
    dateGroup = 'day';
  } else if (duration < 365) {
    dateGroup = 'week';
  } else {
    dateGroup = 'month';
  }
  return dateGroup;
};

const getDateListBetweenStartAndEnd = memoize(
  ({ from, to, dateUnit, hasStartAndEnd = true }) => {
    const dateUnitMap = {
      day: 'days',
      week: 'weeks',
      month: 'months',
    };

    if (!from || !to) return;

    if (!dateUnit) {
      dateUnit = getDateGroupUnit({ from, to });
    }

    let date = [];
    const mFrom = moment(from);
    const mTo = moment(to);

    while (mFrom <= mTo) {
      date.push(mFrom.format('YYYY-MM-DD'));
      mFrom.add(1, dateUnitMap[dateUnit]);
    }

    if (!hasStartAndEnd) {
      date.shift();
      date.pop();
    }

    return date;
  },
  {
    normalizer: JSON.stringify,
    maxAge: 5 * 60 * 1000, // 5 minutes
    max: 1000,
  },
);

const isTodayIncluded = date => {
  return moment(date).format('YYYY-MM-DD') === moment(new Date()).format('YYYY-MM-DD');
};

const parseSecToFullFormat = seconds => {
  return moment.duration(seconds, 'seconds').format('HH:mm:ss', { trim: false });
};

function convertRelativeDate(relativeValue, timeZone = DEFAULT_TIME_ZONE) {
  let { value, unit } = relativeValue;
  let to = utcToZonedTime(new Date(), timeZone);
  let from;

  if (includes(value, ['YTD', 'MTD', 'WTD'])) {
    const unit = DATEFILTER_STATUS_VALUE_MAP[value];
    from = moment().startOf(unit);
  } else if (value === 'LAST_MONTH') {
    const result = getLastMonth(timeZone);
    from = result.from;
    to = result.to;
  } else if (value === 'LAST_QUARTER') {
    const result = getLastQuarter(timeZone);
    from = result.from;
    to = result.to;
  } else {
    if (unit === 'Y') {
      value = value * YEAR - 1;
    } else if (unit === 'M') {
      value = value * 30 - 1;
    } else if (unit === 'D' && value === 1) {
      to = pastNDays(1, to);
    } else if (unit === 'D' && value === 0) {
      to = pastNDays(0, to);
      value = 0;
    } else if (unit === 'D' && value > 0) {
      value = value - 1;
    }

    from = utcToZonedTime(pastNDays(value), timeZone);
  }

  return {
    to: formatter(to),
    from: formatter(from),
  };
}

function getDateFilter({ date, todayExcluded = false, dateFilterValue, savedValue = {}, timeZone }) {
  if (!isValueExist(date)) {
    const [defaultFrom, defaultTo] = getDefaultDate();
    date = { from: defaultFrom, to: defaultTo };
  }

  let originalTo = date.to;

  const isMTD = dateFilterValue === 'MTD';
  const isWTD = dateFilterValue === 'WTD';
  const isYTD = dateFilterValue === 'YTD';
  const isValueStatusKind = isMTD || isWTD || isYTD;

  let { from, to } = date;

  const isToAndFromSame = to === from;

  if (isToAndFromSame && !isValueStatusKind) {
    todayExcluded = false;
    originalTo = to;
  }

  const today = getToday(timeZone);

  // the to date is in the future
  if (moment(to).isAfter(today)) {
    originalTo = to;
    to = todayExcluded ? getYesterday(timeZone) : today;
  }
  const isTodayIncluded = moment(today).isSame(to);

  if (todayExcluded && isTodayIncluded) {
    if (isYTD) {
      const now = getNow(timeZone);
      const yesterday = dateFns.add(now, { days: -1 });
      from = dateFns.format(yesterday, 'yyyy-01-01');
      to = dateFns.format(yesterday, 'yyyy-MM-dd');
    } else if (isValueStatusKind) {
      if (isToAndFromSame) {
        const firstDayOfMonth = moment().format('YYYY-MM-01');
        const isMTDAndFirstDayOfMonth = isMTD && firstDayOfMonth === today;
        const isWTDAndIsMonday = isWTD && moment().weekday() === 1; // Monday

        if (isMTDAndFirstDayOfMonth) {
          /**
           * MTD: 이번달의 첫째날, todayExcluded가 체크되어있는 상태에서
           * 이날 하루를 조회하려고 한다면 "지난 달의 Full date"을 Return
           * */
          const lastMonth = moment().subtract(1, 'months');

          from = lastMonth.startOf('month').format('YYYY-MM-DD');
          to = lastMonth.endOf('month').format('YYYY-MM-DD');
        } else if (isWTDAndIsMonday) {
          /**
           * WTD: 이번주의 월요일, todayExcluded가 체크되어있는 상태에서
           * 이날 하루를 조회하려고 한다면 "지난 주의 Full week"을 Return
           * */
          const lastWeek = moment().subtract(1, 'weeks');

          from = lastWeek.startOf('isoWeek').format('YYYY-MM-DD');
          to = lastWeek.endOf('isoWeek').format('YYYY-MM-DD');
        }
      } else {
        // MTD, WTD. YTD 일때, excludeToday의 to는 제외
        to = moment(to).subtract(1, 'days').format('YYYY-MM-DD');
        originalTo = to;
      }
    } else {
      to = moment(to).subtract(1, 'days').format('YYYY-MM-DD');
      from = moment(from).subtract(1, 'days').format('YYYY-MM-DD');
      originalTo = to;
    }
  }

  const _propOr = propOr('', 'value');

  const isToDateValue = compose(includes(__, ['MTD', 'WTD', 'YTD']), _propOr)(savedValue);

  const dateFilterStatus = isToDateValue ? _propOr(savedValue) : getDateFilterStatus({ date });

  return {
    dateFilter: { from, to },
    dateFilterStatus,
    originalDateFilter: { from, to: originalTo },
  };
}

const DATE_FILTER = ['24H', 'YESTERDAY', '7D', '14D', '30D', '1YEAR', '2YEARS', 'MTD', 'WTD', 'YTD'];

function getDateFilterStatus({ date }) {
  let { from, to } = date;

  if (!(from && to) || !date) {
    return '30D';
  }

  const EQ = equals([from, to]);

  let status = 'pick';

  DATE_FILTER.some(dateUnit => {
    if (EQ(DATES[dateUnit])) {
      status = dateUnit;
      return true;
    }
  });

  return status;
}

const DATES = Object.freeze({
  '24H': [new Date(), new Date()].map(formatter),
  YESTERDAY: [pastNDays(1), pastNDays(1)].map(formatter),
  '7D': [pastNDays(6), new Date()].map(formatter),
  '14D': [pastNDays(13), new Date()].map(formatter),
  '30D': [pastNDays(29), new Date()].map(formatter),
  '1YEAR': [pastNDays(YEAR - 1), new Date()].map(formatter),
  '2YEARS': [pastNDays(YEAR * 2 - 1), new Date()].map(formatter),
  YTD: [moment().startOf('year'), new Date()].map(formatter),
  MTD: [moment().startOf('month'), new Date()].map(formatter),
  WTD: [moment().startOf('isoWeek'), new Date()].map(formatter),
});

const calcComparisonDate = ({ status, comparedDate, dateFilterStatus }) => {
  const { previous, month, quarter, year } = COMPARISON_DATE_FILTER;

  const { from, to } = comparedDate;

  let calcFrom, calcTo;

  switch (status) {
    case previous: {
      const gapDays = moment(to).diff(moment(from), 'days') + 1;

      if (includes(dateFilterStatus, ['MTD', 'WTD', 'YTD'])) {
        const unit = DATEFILTER_STATUS_VALUE_MAP[dateFilterStatus];
        calcFrom = moment(from).subtract(1, unit).format('YYYY-MM-DD');
        calcTo = moment(to).subtract(1, unit).format('YYYY-MM-DD');
      } else if (gapDays === 0) {
        /**
         * @note exception for today vs previous case
         */
        calcFrom = moment(from).subtract(1, 'day').format('YYYY-MM-DD');
        calcTo = moment(to).subtract(1, 'day').format('YYYY-MM-DD');
      } else {
        calcFrom = moment(from).subtract(gapDays, 'day').format('YYYY-MM-DD');
        calcTo = moment(to).subtract(gapDays, 'day').format('YYYY-MM-DD');
      }

      break;
    }
    case month: {
      calcFrom = moment(from).subtract(1, 'month').format('YYYY-MM-DD');
      calcTo = moment(to).subtract(1, 'month').format('YYYY-MM-DD');
      break;
    }
    // Previous version of calc Quarter
    // case quarter: {
    //     const currentQuarter = moment(from).quarter();
    //     const lastQuarter = currentQuarter - 1;

    //     const quarter =
    //         lastQuarter === 0
    //             ? moment(from).subtract(1, 'year').quarter(4)
    //             : moment(from).quarter(lastQuarter);

    //     calcFrom = quarter.startOf('quarter').format('YYYY-MM-DD');
    //     calcTo = quarter.endOf('quarter').format('YYYY-MM-DD');
    //     break;
    // }
    case quarter: {
      calcFrom = moment(from).subtract(3, 'month').format('YYYY-MM-DD');
      calcTo = moment(to).subtract(3, 'month').format('YYYY-MM-DD');
      break;
    }
    case year: {
      calcFrom = moment(from).subtract(1, 'year').format('YYYY-MM-DD');
      calcTo = moment(to).subtract(1, 'year').format('YYYY-MM-DD');
      break;
    }
  }

  return { from: calcFrom, to: calcTo };
};

function getComparisonDateFilter({ date = {}, comparedDate, period = '', dateFilterStatus }) {
  // When it's not comparison mode: default
  let comparisonDateFilter = {};
  let comparisonDateStatus = 'previous';

  // Comparison mode 'relative'
  if (!isValueExist(date) && isValueExist(period)) {
    comparisonDateFilter = calcComparisonDate({
      status: period,
      comparedDate,
      // @ts-ignore
      dateFilterStatus,
    });

    comparisonDateStatus = period;
  } else if (isValueExist(date)) {
    // Comparison mode 'pick' or URL query
    comparisonDateFilter = date;
    comparisonDateStatus = getComparisonDatePeriod({
      dateFilter: comparedDate,
      comparisonDateFilter: date,
    });
  }
  return {
    comparisonDateFilter,
    comparisonDateStatus,
  };
}

const COMPARISON_DATE_FILTER = {
  previous: 'previous',
  month: 'month',
  quarter: 'quarter',
  year: 'year',
  pick: 'pick',
};

const getComparisonDatePeriod = ({ dateFilter, comparisonDateFilter }) => {
  const { from, to } = dateFilter;
  const { from: cfrom, to: cto } = comparisonDateFilter;
  const { previous, month, year, quarter, pick } = COMPARISON_DATE_FILTER;

  const gapDays = moment(to).diff(moment(from), 'days') + 1;

  const calcPrevious = days => moment(days).add(gapDays, 'day').format('YYYY-MM-DD');

  const calcMonth = month => moment(month).add(1, 'month').format('YYYY-MM-DD');

  // Previous version calc quarter
  // const calcQuarter = () => {
  //     const { from, to } = calcComparisonDate({
  //         status: quarter,
  //         comparedDate: dateFilter,
  //     });

  //     const quartercFrom = moment(cfrom)
  //         .startOf('quarter')
  //         .format('YYYY-MM-DD');
  //     const quartercTo = moment(cto).endOf('quarter').format('YYYY-MM-DD');

  //     return (
  //         moment(from).isSame(quartercFrom) && moment(to).isSame(quartercTo)
  //     );
  // };

  const calcQuarter = (month = undefined) => moment(month).add(3, 'month').format('YYYY-MM-DD');

  const calcYear = year => moment(year).add(1, 'year').format('YYYY-MM-DD');

  if (moment(calcPrevious(cfrom)).isSame(from) && moment(calcPrevious(cto)).isSame(to)) {
    return previous;
  } else if (moment(calcMonth(cfrom)).isSame(from) && moment(calcMonth(cto)).isSame(to)) {
    return month;
  } else if (calcQuarter()) {
    return quarter;
  } else if (moment(calcYear(cfrom)).isSame(from) && moment(calcYear(cto)).isSame(to)) {
    return year;
  } else {
    return pick;
  }
};

export function dateFilterWidgetProps(widgetDateFilter: WidgetDateFilter, timeZone: string) {
  const today = getToday(timeZone);
  let { from, to } = widgetDateFilter;
  // the to date is in the future
  if (moment(widgetDateFilter.to).isAfter(today)) {
    to = today;
  }
  return {
    from,
    to,
  };
}

export function dateFilterProps(
  viewConfig: DashboardViewConfig,
  timeZone: string,
): {
  dateFilter: { from: string; to: string };
  comparisonDateFilter: { from: string; to: string } | {};
  [key: string]: any;
} {
  if (!isValueExist(viewConfig)) {
    const [from, to] = getDefaultDate();
    const dateFilter = { from, to };
    const mixedDateFilter = {
      ...dateFilter,
      ...VIEW_DEFAULT_DATE_FILTR,
    };

    return {
      dateFilter,
      mixedDateFilter,
      comparisonDateFilter: {},
      originalDateFilter: dateFilter,
    };
  }

  const payload = pick(['dateFilter', 'comparisonDateFilter'], viewConfig);

  const { dateFilter, comparisonDateFilter } = payload;

  const { type, values } = dateFilter;
  const todayExcluded = 'todayExcluded' in dateFilter ? dateFilter.todayExcluded : true;
  // const dateFilterValue = pathOr('', [type, 'value'], values);
  const dateFilterValue = values[type]?.value ?? '';
  // const dateFilterValue = type == "relative" ? values.relative.value : ''

  /**
   * Date Filter
   */
  const date = equals(type, 'relative') ? convertRelativeDate(values[type], timeZone) : values[type];
  let hasFutureDate = false;
  if (date) {
    const today = getToday(timeZone);
    hasFutureDate = moment(date.to).isAfter(today);
  }
  const {
    dateFilter: parsedDateFilter,
    dateFilterStatus,
    originalDateFilter,
  } = getDateFilter({
    date,
    todayExcluded,
    dateFilterValue,
    savedValue: values[type],
    timeZone,
  });

  const savedComprisonDate = pathOr({}, ['values', 'range', 'period'], comparisonDateFilter);

  /**
   * Comparison Date Filter
   */
  const comparisonType = pathOr('relative', ['type'], comparisonDateFilter);
  const savedComprisonPeriod = pathOr('', ['values', comparisonType, 'period'], comparisonDateFilter);

  const { comparisonDateFilter: parsedComparisonDateFilter, comparisonDateStatus: parsedComparisonDateStatus } =
    getComparisonDateFilter({
      date: comparisonType === 'relative' ? {} : savedComprisonDate,
      comparedDate: hasFutureDate ? date : parsedDateFilter,
      period: savedComprisonPeriod,
      dateFilterStatus,
    });

  return Object.freeze({
    dateFilter: parsedDateFilter,
    dateFilterStatus,
    todayExcluded,
    comparisonDateFilter: parsedComparisonDateFilter,
    comparisonDateStatus: parsedComparisonDateStatus,
    mixedDateFilter: { ...parsedDateFilter, ...dateFilter },
    mixedComparisonDateFilter: {
      ...parsedComparisonDateFilter,
      ...comparisonDateFilter,
    },
    originalDateFilter,
  });
}

/**
 * if the browser doesn't support Intl.DateTimeFormat, we just use the Seoul timezone
 * we can add a polyfill to improve this
 * See: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/DateTimeFormat#see_also
 */
export function getTimezone() {
  const timeZone =
    typeof Intl?.DateTimeFormat === 'undefined' ? DEFAULT_TIME_ZONE : Intl.DateTimeFormat().resolvedOptions().timeZone;
  return timeZone;
}

/**
 * Get current time Date object. Can optionally set the TimeZone.
 * @param timeZone
 * @returns
 */
function getNow(timeZone?: string) {
  const clientTimeZone = getTimezone();
  const utcTime = zonedTimeToUtc(new Date(), clientTimeZone);
  const now = utcToZonedTime(utcTime, timeZone);
  return now;
}

function getToday(timeZone?: string): string {
  /**
   * 1. Get UTC Date
   * 2. Get Date based on the timeZone sent as input
   */
  const now = getNow(timeZone);
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  const formattedDate = `${year}-${month}-${day}`;
  return formattedDate;
}

function getYesterday(timeZone?: string): string {
  /**
   * 1. Get UTC Date
   * 2. Get Date based on the timeZone sent as input
   * 3. Subtract one day
   */
  const now = getNow(timeZone);
  const yesterday = dateFns.add(now, { days: -1 });
  const year = yesterday.getFullYear();
  const month = String(yesterday.getMonth() + 1).padStart(2, '0');
  const day = String(yesterday.getDate()).padStart(2, '0');
  const formattedDate = `${year}-${month}-${day}`;
  return formattedDate;
}

export function getParsedDateFilter(widget, view, timeZone) {
  let originalDateFilter = {};
  let dateFilter: { from?: string; to?: string; dateGroup?: string } = {};
  let comparisonDateFilter: { from?: string; to?: string; dateGroup?: string } = {};

  const isMonthToDate = checkMonthToDate(view?.config?.dateFilter || {});

  if ('dateFilter' in widget && !widget.dateFilter?.disabled) {
    dateFilter = dateFilterWidgetProps(widget.dateFilter, timeZone); // widgets do not support relative date filters yet
    const { from, to } = widget.dateFilter;
    originalDateFilter = { from, to, timeZone, isMonthToDate };
  } else if (widget.type == 'graph') {
    // The reason for if (widget.type == "graph") is because it added dateFilter with the relative object.
    ({ dateFilter, comparisonDateFilter, originalDateFilter } = dateFilterProps(view.config, timeZone));
    comparisonDateFilter = { ...view.config.comparisonDateFilter, ...comparisonDateFilter };
    originalDateFilter = { timeZone, isMonthToDate, ...originalDateFilter };
  } else {
    ({ dateFilter, comparisonDateFilter, originalDateFilter } = dateFilterProps(view.config, timeZone));
    dateFilter = { ...view.config.dateFilter, ...dateFilter };
    comparisonDateFilter = { ...view.config.comparisonDateFilter, ...comparisonDateFilter };
    originalDateFilter = { timeZone, isMonthToDate, ...originalDateFilter };
  }

  return {
    originalDateFilter,
    dateFilter,
    comparisonDateFilter,
  };
}

function checkMonthToDate(dateFilter) {
  return (
    dateFilter?.type === 'relative' &&
    dateFilter?.values?.relative?.value === 'MTD' &&
    dateFilter?.values?.relative?.unit === 'D'
  );
}

function getLastMonth(timeZone = DEFAULT_TIME_ZONE): DateRange {
  return getMonthRange(1, timeZone);
}

function getThisMonth(timeZone = DEFAULT_TIME_ZONE): DateRange {
  return getMonthRange(0, timeZone);
}

function getMonthRange(monthsAgo: number, timeZone = DEFAULT_TIME_ZONE): DateRange {
  const localizedTime = utcToZonedTime(new Date(), timeZone);
  const targetMonth = subMonths(localizedTime, monthsAgo);
  const from = startOfMonth(targetMonth);
  const to = endOfMonth(targetMonth);

  return {
    from,
    to,
  };
}

function getLastQuarter(timeZone = DEFAULT_TIME_ZONE) {
  const localizedTime = utcToZonedTime(new Date(), timeZone);
  const from = startOfQuarter(subQuarters(localizedTime, 1));
  const to = endOfQuarter(subQuarters(localizedTime, 1));

  return {
    from,
    to,
  };
}
